import { Routes } from '@angular/router';

import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';

/**
 * Order matters. The auth feature mounts at '' so its screens keep top-level
 * URLs (/login rather than /auth/login), but a '' route with loadChildren
 * matches by PREFIX — it swallows every URL that reaches it. So it is listed
 * last, and the landing route above it uses pathMatch: 'full' so that the bare
 * '' URL resolves to the landing page instead of falling into the auth feature.
 */
export const routes: Routes = [
    {
        // A signed-in member goes to the dashboard instead of the landing page.
        path: '',
        pathMatch: 'full',
        canMatch: [guestGuard],
        loadComponent: () =>
            import('./features/landing-page/pages/home/home.component').then(m => m.HomeComponent)
    },

    {
        path: 'home',
        canMatch: [guestGuard],
        loadComponent: () =>
            import('./features/landing-page/pages/home/home.component').then(m => m.HomeComponent)
    },

    {
        // The signed-in area (see POST_LOGIN_ROUTE for why it is not "member").
        // canMatch (not canActivate) keeps the chunk from being downloaded at
        // all by a visitor who is not logged in. The shell brings its own
        // sidebar and top bar, so the landing chrome is switched off for every
        // page under it.
        path: 'dashboard',
        canMatch: [authGuard],
        data: { chrome: false },
        loadComponent: () =>
            import('./shared/layouts/dashboard-shell/dashboard-shell.component')
                .then(m => m.DashboardShellComponent),
        children: [
            {
                path: '',
                data: { title: 'Home' },
                loadComponent: () =>
                    import('./features/dashboard/pages/home/dashboard-home.component')
                        .then(m => m.DashboardHomeComponent)
            },
            {
                path: 'profile',
                data: { title: 'Profile' },
                loadComponent: () =>
                    import('./features/dashboard/pages/profile/profile.component')
                        .then(m => m.ProfileComponent)
            },
            {
                // The membership flow, entered from Profile → Plan Setting.
                path: 'profile/plan',
                data: { title: 'Plan', compactPage: true },
                loadComponent: () =>
                    import('./features/dashboard/pages/plan-setting/plan-setting.component')
                        .then(m => m.PlanSettingComponent)
            },
            {
                // Step 1 of 4; the chosen plan rides in ?plan=&billing=.
                path: 'profile/plan/review',
                data: { title: 'Plan', compactPage: true },
                loadComponent: () =>
                    import('./features/dashboard/pages/plan-review/plan-review.component')
                        .then(m => m.PlanReviewComponent)
            },
            {
                // Step 2 of 4.
                path: 'profile/plan/agreement',
                data: { title: 'Plan', compactPage: true },
                loadComponent: () =>
                    import('./features/dashboard/pages/plan-agreement/plan-agreement.component')
                        .then(m => m.PlanAgreementComponent)
            },
            {
                // Step 3 of 4.
                path: 'profile/plan/signature',
                data: { title: 'Plan', compactPage: true },
                loadComponent: () =>
                    import('./features/dashboard/pages/plan-signature/plan-signature.component')
                        .then(m => m.PlanSignatureComponent)
            },
            {
                // The member's current membership. No entry point yet: it needs
                // the membership API to know who is already a member.
                path: 'profile/my-plan',
                data: { title: 'Plan', compactPage: true },
                loadComponent: () =>
                    import('./features/dashboard/pages/my-plan/my-plan.component')
                        .then(m => m.MyPlanComponent)
            },
            {
                // Opened from My Plan.
                path: 'profile/manage-membership',
                data: { title: 'Plan', compactPage: true },
                loadComponent: () =>
                    import('./features/dashboard/pages/manage-membership/manage-membership.component')
                        .then(m => m.ManageMembershipComponent)
            },
            {
                // Step 4 of 4.
                path: 'profile/plan/payment',
                data: { title: 'Plan', compactPage: true },
                loadComponent: () =>
                    import('./features/dashboard/pages/plan-checkout/plan-checkout.component')
                        .then(m => m.PlanCheckoutComponent)
            },
            {
                // Step 4 of 4, online: Stripe's payment form ("Online payment" on Pay).
                path: 'profile/plan/payment/online',
                data: { title: 'Plan', compactPage: true },
                loadComponent: () =>
                    import('./features/dashboard/pages/plan-payment/plan-payment.component')
                        .then(m => m.PlanPaymentComponent)
            },
            {
                // The club's Terms & Conditions, opened from Step 4.
                path: 'profile/plan/terms',
                data: { title: 'Plan', compactPage: true },
                loadComponent: () =>
                    import('./features/dashboard/pages/plan-terms/plan-terms.component')
                        .then(m => m.PlanTermsComponent)
            },
            {
                path: 'personal-information',
                data: { title: 'Personal Information', compactPage: true },
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () =>
                    import('./features/dashboard/components/personal-information/personal-information.component')
                        .then(m => m.PersonalInformationComponent)
            },
        ],
    },

    {
        path: 'book',
        // Preloaded in the background so the booking CTAs open it instantly.
        data: { preload: true },
        loadChildren: () =>
            import('./features/book-appointment/booking.routes')
                .then(m => m.BOOKING_ROUTES)
    },

    {
        // The auth feature owns its own routes, matching the reference feature.
        path: '',
        loadChildren: () =>
            import('./features/auth/auth.routes').then(m => m.AUTH_ROUTES)
    },
];
